/*!
 * chq-collection-bss.js
 * v2.0 — 2026-04-20
 * "Hard fix" for BSS B2B Quote "Request Bulk Quote" button position
 * on CasterHQ collection pages.
 *
 * Strategy change from v1:
 *   v1 moved the BSS wrapper → BSS re-injected a new one at the original
 *   mount → duplicates (up to 9 per card). Bad.
 *
 *   v2 enforces the invariant, continuously:
 *     "Per card: exactly ONE <bcp-r4q-button-wrapper>, placed immediately
 *      after .call-to-action. Any others are deleted."
 *   If BSS re-injects, the MutationObserver fires, we delete the new one,
 *   keeping our keeper. BSS typically gives up re-injecting after its
 *   initial mount pass, so this settles quickly.
 *
 *   No retry loop (v1's retry loop compounded duplicates).
 *   No flex-order tricks. No position:absolute. Pure DOM move + dedupe.
 *
 *   Safety:
 *     - Bails immediately on product pages (template-product / .product)
 *     - rAF-debounced observer (never fights BSS in same frame)
 *     - Move-count cap per card (50) → if BSS is fighting us forever,
 *       we stop moving and just keep deleting duplicates
 *     - Collection scope guard
 */
(function () {
  'use strict';

  // --- scope guard: NEVER run on product pages -------------------------
  function isCollectionScope() {
    var b = document.body;
    if (!b) return false;
    if (b.classList.contains('product')) return false;
    if (b.classList.contains('template-product')) return false;
    if (b.classList.contains('collection')) return true;
    if (b.classList.contains('template-collection')) return true;
    if (b.classList.contains('search')) return true;
    if (b.classList.contains('template-search')) return true;
    // Any page with >=2 product-wrap cards (vendor/tag pages)
    if (document.querySelectorAll('.product-wrap .call-to-action').length >= 2) return true;
    return false;
  }

  function qAll(sel, root) { return (root || document).querySelectorAll(sel); }

  // Per-card state: move count cap
  var moveCounts = new WeakMap();
  var MAX_MOVES = 50;

  function enforceCard(card) {
    if (!card || !card.querySelector) return;
    var cta = card.querySelector(':scope > .product-wrap > .call-to-action')
           || card.querySelector('.call-to-action');
    if (!cta) return;

    var wrappers = Array.prototype.slice.call(
      card.querySelectorAll('bcp-r4q-button-wrapper')
    );
    if (!wrappers.length) return;

    // Pick keeper: prefer the one already marked, else the one already
    // sitting as next sibling of CTA, else the first.
    var keeper = null;
    for (var i = 0; i < wrappers.length; i++) {
      if (wrappers[i].dataset.chqKeeper === '1') { keeper = wrappers[i]; break; }
    }
    if (!keeper) {
      for (var j = 0; j < wrappers.length; j++) {
        if (cta.nextElementSibling === wrappers[j]) { keeper = wrappers[j]; break; }
      }
    }
    if (!keeper) keeper = wrappers[0];

    // Position keeper below CTA if not already there
    if (cta.nextElementSibling !== keeper) {
      var count = (moveCounts.get(card) || 0);
      if (count < MAX_MOVES) {
        try {
          cta.insertAdjacentElement('afterend', keeper);
          moveCounts.set(card, count + 1);
        } catch (e) {}
      }
    }
    keeper.dataset.chqKeeper = '1';
    keeper.classList.add('chq-bss-keeper');

    // Delete duplicates
    for (var k = 0; k < wrappers.length; k++) {
      if (wrappers[k] !== keeper) {
        try { wrappers[k].remove(); } catch (e) {}
      }
    }
  }

  // --- observers --------------------------------------------------------
  var observed = new WeakSet();

  function attachCardObserver(card) {
    if (observed.has(card)) return;
    observed.add(card);
    var pending = false;
    try {
      var mo = new MutationObserver(function () {
        if (pending) return;
        pending = true;
        requestAnimationFrame(function () {
          pending = false;
          enforceCard(card);
        });
      });
      mo.observe(card, { childList: true, subtree: true });
    } catch (e) {}
  }

  function scan() {
    var cards = qAll('.product-wrap');
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      var boundary = card.parentElement &&
                     card.parentElement.classList &&
                     card.parentElement.classList.contains('one-fourth')
        ? card.parentElement
        : card;
      enforceCard(boundary);
      attachCardObserver(boundary);
    }
  }

  // Top-level observer for AJAX pagination / filter updates
  function watchGrid() {
    var grid = document.querySelector(
      '[data-collection-main], .collection-matrix, .collection-matrix__wrapper, .collection__main'
    ) || document.body;
    try {
      var pending = false;
      var mo = new MutationObserver(function () {
        if (pending) return;
        pending = true;
        requestAnimationFrame(function () { pending = false; scan(); });
      });
      mo.observe(grid, { childList: true, subtree: true });
    } catch (e) {}
  }

  function run() {
    if (!isCollectionScope()) return;
    scan();
    watchGrid();
    // Short boot-phase scans only (NOT a 15s loop — that caused duplicates)
    setTimeout(scan, 150);
    setTimeout(scan, 500);
    setTimeout(scan, 1200);
    setTimeout(scan, 3000);
    window.addEventListener('load', function () { setTimeout(scan, 150); });
    document.addEventListener('shopify:section:load', function () { setTimeout(scan, 50); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
